import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useSimulator } from "@/context/SimulatorContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PRESET_SCENARIOS } from "@/lib/defaults";
import { FolderKanban, Save, Trash2, Upload, RefreshCw, Sparkles, FileText } from "lucide-react";
import { fmtEUR } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ScenariosTab = () => {
  const { inputs, summary, loadPreset, loadInputs, resetInputs } = useSimulator();
  const [scenarios, setScenarios] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchScenarios = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/scenarios`);
      setScenarios(res.data);
    } catch (e) {
      toast.error("Errore nel caricamento scenari");
    }
  }, []);

  useEffect(() => {
    fetchScenarios();
  }, [fetchScenarios]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Inserisci un nome scenario");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/scenarios`, {
        name: name.trim(),
        description: description.trim(),
        inputs,
      });
      toast.success(`Scenario "${name}" salvato`);
      setName("");
      setDescription("");
      fetchScenarios();
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally {
      setLoading(false);
    }
  };

  const handleLoad = (scenario) => {
    loadInputs(scenario.inputs, scenario.name);
    toast.success(`Scenario "${scenario.name}" caricato`);
  };

  const handleDelete = async (id, name) => {
    try {
      await axios.delete(`${API}/scenarios/${id}`);
      toast.success(`Scenario "${name}" eliminato`);
      fetchScenarios();
    } catch (e) {
      toast.error("Errore nell'eliminazione");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-4">
        <div className="rounded-xl bg-sky-500/10 border border-sky-500/30 p-3">
          <FolderKanban className="h-7 w-7 text-sky-400" />
        </div>
        <div>
          <h2 className="font-display text-2xl font-bold text-slate-100">
            Gestione Scenari
          </h2>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Salva, ricarica e confronta simulazioni. Ideale per presentare
            proiezioni base / ottimistiche / conservative agli investitori.
          </p>
        </div>
      </div>

      <Card className="bg-slate-900/70 border-slate-800 p-6">
        <h3 className="font-display text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-amber-400" />
          Preset predefiniti
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.entries(PRESET_SCENARIOS).map(([key, preset]) => (
            <button
              key={key}
              data-testid={`preset-${key}`}
              onClick={() => {
                loadPreset(key);
                toast.success(`Preset "${preset.name}" caricato`);
              }}
              className="text-left p-4 rounded-lg border border-slate-800 bg-slate-950/50 hover:border-emerald-500/40 hover:bg-slate-900 transition-all group"
            >
              <div className="font-display font-semibold text-slate-100 group-hover:text-emerald-300 transition-colors">
                {preset.name}
              </div>
              <div className="mt-1 text-xs text-slate-500 line-clamp-2">
                {preset.description}
              </div>
            </button>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            data-testid="reset-scenario-button"
            variant="outline"
            size="sm"
            onClick={() => {
              resetInputs();
              toast.info("Parametri resettati ai valori di fabbrica");
            }}
            className="border-slate-700 bg-slate-950 hover:bg-slate-900 text-slate-200"
          >
            <RefreshCw className="h-3.5 w-3.5 mr-2" />
            Reset ai valori di fabbrica
          </Button>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-slate-900/70 border-slate-800 p-6 space-y-4">
          <h3 className="font-display text-lg font-semibold text-slate-100 flex items-center gap-2">
            <Save className="h-4 w-4 text-emerald-400" />
            Salva scenario corrente
          </h3>

          <div className="text-xs text-slate-500 border border-slate-800 rounded-lg p-3 bg-slate-950/50">
            <div className="flex justify-between">
              <span>Ricavo attuale:</span>
              <span className="font-mono-tab text-emerald-400">{fmtEUR(summary.grossRevenue)}</span>
            </div>
            <div className="flex justify-between">
              <span>Margine:</span>
              <span className="font-mono-tab text-sky-400">{fmtEUR(summary.operatingMargin)}</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Nome scenario
            </Label>
            <Input
              data-testid="scenario-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="es. Pitch investitori Q1 2026"
              className="bg-slate-950 border-slate-800 text-slate-100"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Descrizione (opzionale)
            </Label>
            <Textarea
              data-testid="scenario-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ipotesi di crescita per il round seed..."
              className="bg-slate-950 border-slate-800 text-slate-100 min-h-[80px]"
            />
          </div>

          <Button
            data-testid="save-scenario-button"
            onClick={handleSave}
            disabled={loading}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-semibold"
          >
            <Save className="h-4 w-4 mr-2" />
            Salva scenario
          </Button>
        </Card>

        <Card className="bg-slate-900/70 border-slate-800 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold text-slate-100 flex items-center gap-2">
              <FileText className="h-4 w-4 text-sky-400" />
              Scenari salvati
              <span className="text-xs font-mono-tab text-slate-500 font-normal">
                ({scenarios.length})
              </span>
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchScenarios}
              className="text-slate-400 hover:text-slate-100"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
          </div>

          {scenarios.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-500 border border-dashed border-slate-800 rounded-lg">
              Nessuno scenario salvato. Salva la simulazione corrente per
              iniziare.
            </div>
          ) : (
            <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
              {scenarios.map((s) => (
                <div
                  key={s.id}
                  data-testid={`scenario-item-${s.id}`}
                  className="p-3 rounded-lg border border-slate-800 bg-slate-950/50 hover:border-slate-700 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-semibold text-slate-100 truncate">
                        {s.name}
                      </div>
                      {s.description && (
                        <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                          {s.description}
                        </div>
                      )}
                      <div className="text-[10px] text-slate-600 mt-1 font-mono-tab uppercase tracking-wider">
                        {new Date(s.created_at).toLocaleString("it-IT")}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        data-testid={`load-scenario-${s.id}`}
                        size="sm"
                        variant="ghost"
                        onClick={() => handleLoad(s)}
                        className="h-8 text-sky-400 hover:text-sky-300 hover:bg-sky-500/10"
                      >
                        <Upload className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        data-testid={`delete-scenario-${s.id}`}
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(s.id, s.name)}
                        className="h-8 text-rose-400 hover:text-rose-300 hover:bg-rose-500/10"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
